import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  api,
  formatDate,
  photoUrl,
  type SpecimenListItem,
  type Trip,
} from '../api.ts';
import { Icon } from '../components/Icon.tsx';

/** Экран выезда: что привезли и откуда. Место здесь одно на всю партию. */
export function TripScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [trip, setTrip] = useState<(Trip & { specimens: SpecimenListItem[] }) | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api.trip(id).then(setTrip).catch((e: Error) => setError(e.message));
  }, [id]);

  if (error) {
    return (
      <div className="app">
        <div className="scroll muted small">{error}</div>
      </div>
    );
  }
  if (!trip) {
    return (
      <div className="app">
        <div className="scroll muted small">Загрузка…</div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="topbar">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Назад">
          <Icon name="back" className="icon muted" />
        </button>
        <div>
          <h2 className="topbar-title">{trip.title ?? trip.locality_name ?? 'Выезд'}</h2>
          <div className="sub">
            {formatDate(trip.started_on)} · {trip.specimen_count} образцов
          </div>
        </div>
        <div className="spacer" />
        <Link className="icon-btn" to={`/trips/${trip.id}/edit`} aria-label="Редактировать">
          <Icon name="edit" className="icon muted" />
        </Link>
      </div>

      <div className="scroll">
        <dl className="rows">
          {trip.locality_name && (
            <div className="row">
              <dt>
                <Icon name="pin" className="icon icon-sm" />
                Месторождение
              </dt>
              <dd>
                {[trip.locality_name, trip.locality_country].filter(Boolean).join(', ')}
                {trip.locality_note && <div className="hint">{trip.locality_note}</div>}
              </dd>
            </div>
          )}
          {trip.latitude && (
            <div className="row">
              <dt>
                <Icon name="pin" className="icon icon-sm" />
                Координаты
              </dt>
              <dd>
                {Number(trip.latitude).toFixed(5)}, {Number(trip.longitude).toFixed(5)}
                {trip.coord_precision_m != null && (
                  <div className="hint">±{trip.coord_precision_m} м</div>
                )}
              </dd>
            </div>
          )}
          {trip.companions && (
            <div className="row">
              <dt>
                <Icon name="route" className="icon icon-sm" />
                Спутники
              </dt>
              <dd>{trip.companions}</dd>
            </div>
          )}
        </dl>

        {trip.notes && (
          <>
            <div className="label-key">Заметки о поездке</div>
            <p className="notes">{trip.notes}</p>
          </>
        )}

        <div className="label-key">Привезено</div>
        {trip.specimens.length === 0 ? (
          <div className="muted small">Пока ничего не привязано к этому выезду.</div>
        ) : (
          <div className="tiles">
            {trip.specimens.map((item) => (
              <Link key={item.id} className="tile" to={`/specimen/${item.id}`}>
                <div className="well">
                  {item.cover_photo_id ? (
                    <img
                      className="tile-cover"
                      src={photoUrl(item.cover_photo_id, true)}
                      alt=""
                      loading="lazy"
                    />
                  ) : (
                    <Icon name="photo" className="icon" />
                  )}
                </div>
                <div className="inv">{item.inv_number}</div>
                <div className="name">{item.primary_mineral ?? '—'}</div>
                <div className="meta">{item.variety ?? ' '}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
